import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PropertySourceModal } from './PropertySourceModal'
import { supabase } from "@/integrations/supabase/client"
import { useMailboxProperties } from '@/hooks/useMailboxProperties'
import { 
  Database, 
  Search, 
  Building2, 
  UserCheck, 
  Globe, 
  Settings,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
  Eye,
  EyeOff,
  Shield,
  Layers,
  GitMerge,
  Mail,
  Server,
  RefreshCw
} from 'lucide-react'

interface Property {
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'jsonb' | 'array'
  required: boolean
  nullable: boolean
  default_value?: string
  description?: string
  section?: string
}

interface EntityProperties {
  [key: string]: Property[]
}

export function PropertiesTab() {
  const { properties: unifiedMailboxProperties, getAllSections } = useMailboxProperties()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEntity, setSelectedEntity] = useState('accounts')
  const [showSystemProperties, setShowSystemProperties] = useState(false)
  const [masterFields, setMasterFields] = useState<Set<string>>(new Set())
  const [selectedProperty, setSelectedProperty] = useState<{ name: string; tableName: string } | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)

  // Define properties for the unified accounts entity (includes companies, agencies, and SaaS apps)
  const entityProperties: EntityProperties = {
    accounts: [
      // Core Business Information
      { name: 'company_name', label: 'Company Name', type: 'text', required: true, nullable: false, section: 'Core Information' },
      { name: 'domain', label: 'Domain', type: 'text', required: false, nullable: true, section: 'Core Information' },
      { name: 'industry', label: 'Industry', type: 'text', required: false, nullable: true, section: 'Core Information' },
      { name: 'employee_count', label: 'Company Size', type: 'text', required: false, nullable: true, section: 'Core Information' },
      { name: 'description', label: 'Description', type: 'text', required: false, nullable: true, section: 'Core Information' },
      { name: 'account_type', label: 'Account Type', type: 'select', required: false, nullable: false, section: 'Core Information', default_value: 'company', description: 'Type of account: company, agency, or saas_app' },
      
      // Financial Data
      { name: 'monthly_revenue', label: 'Monthly Revenue', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      { name: 'annual_revenue', label: 'Annual Revenue', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      { name: 'customer_lifetime_value', label: 'Customer Lifetime Value', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      { name: 'churn_rate', label: 'Churn Rate', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      { name: 'return_rate', label: 'Return Rate', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      { name: 'subscription_revenue', label: 'Subscription Revenue', type: 'number', required: false, nullable: true, section: 'Financial Data' },
      
      // Location Information
      { name: 'country', label: 'Country', type: 'text', required: false, nullable: true, section: 'Location' },
      { name: 'state', label: 'State', type: 'text', required: false, nullable: true, section: 'Location' },
      { name: 'city', label: 'City', type: 'text', required: false, nullable: true, section: 'Location' },
      { name: 'address', label: 'Address', type: 'text', required: false, nullable: true, section: 'Location' },
      { name: 'postal_code', label: 'Postal Code', type: 'text', required: false, nullable: true, section: 'Location' },
      { name: 'company_location', label: 'Company Location', type: 'text', required: false, nullable: true, section: 'Location' },
      
      // Contact Information
      { name: 'phone', label: 'Phone', type: 'text', required: false, nullable: true, section: 'Contact Information' },
      { name: 'website_url', label: 'Website URL', type: 'text', required: false, nullable: true, section: 'Contact Information' },
      { name: 'linkedin_url', label: 'LinkedIn URL', type: 'text', required: false, nullable: true, section: 'Contact Information' },
      { name: 'contact_emails', label: 'Contact Emails', type: 'jsonb', required: false, nullable: true, section: 'Contact Information' },
      
      // Agency-Specific Properties
      { name: 'services_offered', label: 'Services Offered', type: 'jsonb', required: false, nullable: true, section: 'Agency Information', description: 'Services provided by agencies' },
      { name: 'specialties', label: 'Specialties', type: 'jsonb', required: false, nullable: true, section: 'Agency Information', description: 'Areas of expertise for agencies' },
      { name: 'founded_year', label: 'Founded Year', type: 'number', required: false, nullable: true, section: 'Agency Information' },
      
      // Technology Stack
      { name: 'tech_stack_online', label: 'Tech Stack Online', type: 'jsonb', required: false, nullable: true, section: 'Technology' },
      { name: 'tech_stack_retail', label: 'Tech Stack Retail', type: 'jsonb', required: false, nullable: true, section: 'Technology' },
      { name: 'ecommerce_platform', label: 'E-commerce Platform', type: 'text', required: false, nullable: true, section: 'Technology' },
      { name: 'shopify_plan', label: 'Shopify Plan', type: 'text', required: false, nullable: true, section: 'Technology' },
      { name: 'platform_domain', label: 'Platform Domain', type: 'text', required: false, nullable: true, section: 'Technology' },
      
      // Business Intelligence & Metrics
      { name: 'monthly_order_volume', label: 'Monthly Order Volume', type: 'number', required: false, nullable: true, section: 'Business Metrics' },
      { name: 'monthly_order_volume_estimate', label: 'Monthly Order Volume (Est)', type: 'number', required: false, nullable: true, section: 'Business Metrics' },
      { name: 'annual_return_volume', label: 'Annual Return Volume', type: 'number', required: false, nullable: true, section: 'Business Metrics' },
      { name: 'physical_locations_count', label: 'Physical Locations Count', type: 'number', required: false, nullable: true, section: 'Business Metrics' },
      { name: 'customer_satisfaction_score', label: 'Customer Satisfaction Score', type: 'number', required: false, nullable: true, section: 'Business Metrics' },
      
      // Partnerships & Relationships
      { name: 'investors', label: 'Investors', type: 'jsonb', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'board_members', label: 'Board Members', type: 'jsonb', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'retailers', label: 'Retailers', type: 'jsonb', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'email_sms_agency', label: 'Email/SMS Agency', type: 'text', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'performance_marketing_agency', label: 'Performance Marketing Agency', type: 'text', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'web_dev_agency', label: 'Web Dev Agency', type: 'text', required: false, nullable: true, section: 'Business Relationships' },
      { name: 'cro_agency', label: 'CRO Agency', type: 'text', required: false, nullable: true, section: 'Business Relationships' },
      
      // Reviews & Ratings
      { name: 'trustpilot_rating', label: 'Trustpilot Rating', type: 'number', required: false, nullable: true, section: 'Reviews & Ratings' },
      { name: 'trustpilot_review_count', label: 'Trustpilot Review Count', type: 'number', required: false, nullable: true, section: 'Reviews & Ratings' },
      { name: 'yotpo_rating', label: 'Yotpo Rating', type: 'number', required: false, nullable: true, section: 'Reviews & Ratings' },
      { name: 'yotpo_review_count', label: 'Yotpo Review Count', type: 'number', required: false, nullable: true, section: 'Reviews & Ratings' },
      
      // Status & Management
      { name: 'account_status', label: 'Account Status', type: 'select', required: false, nullable: true, section: 'Status & Management' },
      { name: 'account_owner', label: 'Account Owner', type: 'text', required: false, nullable: true, section: 'Status & Management' },
      { name: 'priority_score', label: 'Priority Score', type: 'number', required: false, nullable: true, section: 'Status & Management' },
      { name: 'is_owned_account', label: 'Is Owned Account', type: 'boolean', required: false, nullable: true, section: 'Status & Management' },
      { name: 'in_sales_cycle', label: 'In Sales Cycle', type: 'boolean', required: false, nullable: true, section: 'Status & Management' },
      { name: 'is_customer', label: 'Is Customer', type: 'boolean', required: false, nullable: true, section: 'Status & Management' },
      
      // Enrichment & Data Sources
      { name: 'linkedin_data_pull', label: 'LinkedIn Data Pull', type: 'boolean', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'linkedin_data_pull_date', label: 'LinkedIn Data Pull Date', type: 'date', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'linkedin_enrichment_data', label: 'LinkedIn Enrichment Data', type: 'jsonb', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'storeleads_enriched', label: 'StoreLeads Enriched', type: 'boolean', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'storeleads_enriched_date', label: 'StoreLeads Enriched Date', type: 'date', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'storeleads_enrichment_data', label: 'StoreLeads Enrichment Data', type: 'jsonb', required: false, nullable: true, section: 'Enrichment Data' },
      
      // Activity Tracking
      { name: 'last_activity_date', label: 'Last Activity Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_direct_email_date', label: 'Last Direct Email Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_soft_email_date', label: 'Last Soft Email Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_cold_call_date', label: 'Last Cold Call Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_ad_clicked_date', label: 'Last Ad Clicked Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_webinar_engaged_date', label: 'Last Webinar Engaged Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      { name: 'last_important_activity_date', label: 'Last Important Activity Date', type: 'date', required: false, nullable: true, section: 'Activity Tracking' },
      
      // Business Context
      { name: 'peak_season_start', label: 'Peak Season Start', type: 'date', required: false, nullable: true, section: 'Business Context' },
      { name: 'peak_season_end', label: 'Peak Season End', type: 'date', required: false, nullable: true, section: 'Business Context' },
      { name: 'founded_date', label: 'Founded Date', type: 'date', required: false, nullable: true, section: 'Business Context' },
      { name: 'last_product_published_date', label: 'Last Product Published', type: 'date', required: false, nullable: true, section: 'Business Context' },
      { name: 'currency_code', label: 'Currency Code', type: 'text', required: false, nullable: true, section: 'Business Context' },
      { name: 'in_market_region', label: 'In Market Region', type: 'text', required: false, nullable: true, section: 'Business Context' },
      
      // Sales & Marketing
      { name: 'lead_source', label: 'Lead Source', type: 'text', required: false, nullable: true, section: 'Sales & Marketing' },
      { name: 'tam_tier', label: 'TAM Tier', type: 'text', required: false, nullable: true, section: 'Sales & Marketing' },
      { name: 'client_tam', label: 'Client TAM', type: 'text', required: false, nullable: true, section: 'Sales & Marketing' },
      { name: 'product', label: 'Product', type: 'text', required: false, nullable: true, section: 'Sales & Marketing' },
      
      // External References
      { name: 'hubspot_id', label: 'HubSpot ID', type: 'text', required: false, nullable: true, section: 'External References' },
      { name: 'company_logo_url', label: 'Company Logo URL', type: 'text', required: false, nullable: true, section: 'External References' },
      { name: 'linkedin_company_image_url', label: 'LinkedIn Company Image URL', type: 'text', required: false, nullable: true, section: 'External References' },
      { name: 'social_media_links', label: 'Social Media Links', type: 'jsonb', required: false, nullable: true, section: 'External References' },
      
      // Suitability Analysis
      { name: 'suitability_status', label: 'Suitability Status', type: 'text', required: false, nullable: true, section: 'Suitability Analysis' },
      { name: 'suitability_reason', label: 'Suitability Reason', type: 'text', required: false, nullable: true, section: 'Suitability Analysis' },
      { name: 'suitability_confidence', label: 'Suitability Confidence', type: 'number', required: false, nullable: true, section: 'Suitability Analysis' },
      { name: 'suitability_analysis_date', label: 'Suitability Analysis Date', type: 'date', required: false, nullable: true, section: 'Suitability Analysis' },
      { name: 'suitability_prompt', label: 'Suitability Prompt', type: 'text', required: false, nullable: true, section: 'Suitability Analysis' },
      
      // System Properties
      { name: 'id', label: 'ID', type: 'text', required: true, nullable: false, section: 'System', default_value: 'gen_random_uuid()' },
      { name: 'client_id', label: 'Client ID', type: 'number', required: true, nullable: false, section: 'System' },
      { name: 'created_at', label: 'Created At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
      { name: 'updated_at', label: 'Updated At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
    ],
    
    contacts: [
      // Core Information
      { name: 'first_name', label: 'First Name', type: 'text', required: true, nullable: false, section: 'Core Information' },
      { name: 'last_name', label: 'Last Name', type: 'text', required: true, nullable: false, section: 'Core Information' },
      { name: 'email', label: 'Email', type: 'text', required: true, nullable: false, section: 'Contact Information' },
      { name: 'phone', label: 'Phone', type: 'text', required: false, nullable: true, section: 'Contact Information' },
      
      // Professional Information
      { name: 'job_title', label: 'Job Title', type: 'text', required: false, nullable: true, section: 'Professional' },
      { name: 'department', label: 'Department', type: 'text', required: false, nullable: true, section: 'Professional' },
      { name: 'seniority_level', label: 'Seniority Level', type: 'text', required: false, nullable: true, section: 'Professional' },
      
      // LinkedIn & Enrichment
      { name: 'linkedin_profile_url', label: 'LinkedIn Profile', type: 'text', required: false, nullable: true, section: 'Social Media' },
      { name: 'linkedin_search_status', label: 'LinkedIn Search Status', type: 'select', required: false, nullable: true, section: 'Enrichment Data' },
      { name: 'linkedin_enrichment_status', label: 'LinkedIn Enrichment Status', type: 'select', required: false, nullable: true, section: 'Enrichment Data' },
      
      // Status & Engagement
      { name: 'contact_status', label: 'Contact Status', type: 'select', required: false, nullable: true, section: 'Status' },
      { name: 'engagement_status', label: 'Engagement Status', type: 'select', required: false, nullable: true, section: 'Status' },
      
      // System Properties
      { name: 'id', label: 'ID', type: 'text', required: true, nullable: false, section: 'System', default_value: 'gen_random_uuid()' },
      { name: 'client_id', label: 'Client ID', type: 'number', required: true, nullable: false, section: 'System' },
      { name: 'created_at', label: 'Created At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
      { name: 'updated_at', label: 'Updated At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
    ],

    domains: [
      // Core Information
      { name: 'domain', label: 'Domain Name', type: 'text', required: true, nullable: false, section: 'Core Information' },
      { name: 'client_id', label: 'Client ID', type: 'number', required: false, nullable: true, section: 'Core Information' },
      { name: 'domain_type', label: 'Domain Type', type: 'select', required: false, nullable: true, section: 'Core Information', description: 'Primary, secondary, etc.' },
      
      // Mailbox Statistics
      { name: 'total_mailboxes', label: 'Total Mailboxes', type: 'number', required: false, nullable: true, section: 'Mailbox Statistics' },
      { name: 'active_mailboxes', label: 'Active Mailboxes', type: 'number', required: false, nullable: true, section: 'Mailbox Statistics' },
      { name: 'warmup_mailboxes', label: 'Warmup Mailboxes', type: 'number', required: false, nullable: true, section: 'Mailbox Statistics' },
      
      // DNS Configuration
      { name: 'mx_records', label: 'MX Records', type: 'jsonb', required: false, nullable: true, section: 'DNS Configuration' },
      { name: 'spf_record', label: 'SPF Record', type: 'text', required: false, nullable: true, section: 'DNS Configuration' },
      { name: 'dkim_record', label: 'DKIM Record', type: 'text', required: false, nullable: true, section: 'DNS Configuration' },
      { name: 'dmarc_record', label: 'DMARC Record', type: 'text', required: false, nullable: true, section: 'DNS Configuration' },
      
      // Status & Health
      { name: 'status', label: 'Domain Status', type: 'select', required: false, nullable: true, section: 'Status & Health' },
      { name: 'health_score', label: 'Health Score', type: 'number', required: false, nullable: true, section: 'Status & Health' },
      { name: 'reputation_score', label: 'Reputation Score', type: 'number', required: false, nullable: true, section: 'Status & Health' },
      
      // System Properties
      { name: 'id', label: 'ID', type: 'text', required: true, nullable: false, section: 'System', default_value: 'gen_random_uuid()' },
      { name: 'created_at', label: 'Created At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
      { name: 'updated_at', label: 'Updated At', type: 'date', required: true, nullable: false, section: 'System', default_value: 'now()' },
    ],

    mailboxes: unifiedMailboxProperties.map(prop => ({
      name: prop.id,
      label: prop.label,
      type: prop.type,
      required: prop.required || false,
      nullable: !prop.required,
      section: prop.section,
      description: prop.description
    }))
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'text':
        return <Type className="w-4 h-4 text-blue-500" />
      case 'number':
        return <Hash className="w-4 h-4 text-green-500" />
      case 'date':
        return <Calendar className="w-4 h-4 text-purple-500" />
      case 'boolean':
        return <ToggleLeft className="w-4 h-4 text-orange-500" />
      case 'select':
        return <List className="w-4 h-4 text-pink-500" />
      case 'jsonb':
      case 'array':
        return <Database className="w-4 h-4 text-indigo-500" />
      default:
        return <Type className="w-4 h-4 text-gray-500" />
    }
  }

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'accounts':
        return <Building2 className="h-4 w-4" />
      case 'contacts':
        return <UserCheck className="h-4 w-4" />
      case 'domains':
        return <Globe className="h-4 w-4" />
      case 'mailboxes':
        return <Mail className="h-4 w-4" />
      case 'agencies':
        return <Server className="h-4 w-4" />
      default:
        return <Database className="h-4 w-4" />
    }
  }

  const getEntityLabel = (entityType: string) => {
    switch (entityType) {
      case 'accounts':
        return 'Accounts'
      case 'contacts':
        return 'Contacts'
      case 'domains':
        return 'Domains'
      case 'mailboxes':
        return 'Mailboxes'
      case 'agencies':
        return 'Agencies'
      default:
        return entityType.charAt(0).toUpperCase() + entityType.slice(1)
    }
  }

  const filterProperties = (properties: Property[]) => {
    let filtered = properties
    
    if (!showSystemProperties) {
      filtered = filtered.filter(prop => prop.section !== 'System')
    }
    
    if (searchQuery) {
      filtered = filtered.filter(prop => 
        prop.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prop.type.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    
    return filtered
  }

  const groupPropertiesBySection = (properties: Property[]) => {
    const sections: { [key: string]: Property[] } = {}
    
    properties.forEach(property => {
      const section = property.section || 'Other'
      if (!sections[section]) {
        sections[section] = []
      }
      sections[section].push(property)
    })
    
    return sections
  }

  // Handle property schema sync
  const handleSyncProperties = async () => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-crm-properties', {
        body: { table_name: selectedEntity }
      });

      if (error) throw error;
      
      console.log('Schema sync completed:', data);
      // You could update the UI here with discovered properties
      // For now, we just log the results
    } catch (error) {
      console.error('Error syncing properties:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Fetch master fields that have multiple sources
  useEffect(() => {
    const fetchMasterFields = async () => {
      try {
        const { data, error } = await supabase
          .from('property_priority_rules')
          .select('property_name')
          .eq('is_active', true);

        if (error) throw error;
        
        // Get unique property names that have priority rules (indicating multiple sources)
        const uniqueProperties = [...new Set(data?.map(rule => rule.property_name) || [])];
        setMasterFields(new Set(uniqueProperties));
      } catch (error) {
        console.error('Error fetching master fields:', error);
      }
    };

    fetchMasterFields();
  }, []);

  const currentProperties = entityProperties[selectedEntity] || []
  const filteredProperties = filterProperties(currentProperties)
  const groupedProperties = groupPropertiesBySection(filteredProperties)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Database className="h-6 w-6" />
                CRM Properties
              </CardTitle>
              <p className="text-muted-foreground mt-1">
                View and manage all properties across your CRM entities. The unified Accounts entity includes companies, agencies, and SaaS apps differentiated by the 'account_type' property.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncProperties}
                disabled={isSyncing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Syncing...' : 'Sync Schema'}
              </Button>
              <Button
                variant={showSystemProperties ? "default" : "outline"}
                size="sm"
                onClick={() => setShowSystemProperties(!showSystemProperties)}
                className="flex items-center gap-2"
              >
                {showSystemProperties ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                {showSystemProperties ? "Hide System" : "Show System"}
              </Button>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search properties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedEntity} onValueChange={setSelectedEntity} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              {Object.keys(entityProperties).map((entityType) => (
                <TabsTrigger key={entityType} value={entityType} className="flex items-center gap-2">
                  {getEntityIcon(entityType)}
                  {getEntityLabel(entityType)}
                  <Badge variant="outline" className="ml-2">
                    {filterProperties(entityProperties[entityType]).length}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.keys(entityProperties).map((entityType) => (
              <TabsContent key={entityType} value={entityType} className="space-y-6 mt-6">
                {Object.entries(groupedProperties).map(([sectionName, sectionProperties]) => (
                  <div key={sectionName} className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <h4 className="font-semibold text-sm">{sectionName}</h4>
                      <Badge variant="outline" className="text-xs">
                        {sectionProperties.length} properties
                      </Badge>
                    </div>
                    
                    <div className="grid gap-3">
                      {sectionProperties.map((property) => {
                        const isMasterField = masterFields.has(property.name);
                        
                        return (
                          <div key={property.name} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3 flex-1">
                              {getTypeIcon(property.type)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-medium text-sm">{property.label}</h5>
                                  {property.required && (
                                    <Badge variant="destructive" className="text-xs h-5 px-1">
                                      Required
                                    </Badge>
                                  )}
                                  {!property.nullable && (
                                    <Badge variant="secondary" className="text-xs h-5 px-1">
                                      Not Null
                                    </Badge>
                                  )}
                                  {isMasterField && (
                                    <Badge variant="default" className="text-xs h-5 px-1 bg-primary/10 text-primary border-primary/20">
                                      <Layers className="h-3 w-3 mr-1" />
                                      Master Field
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>Field name: <code className="bg-muted px-1 rounded">{property.name}</code></span>
                                  <span>Type: <code className="bg-muted px-1 rounded">{property.type}</code></span>
                                  {property.default_value && (
                                    <span>Default: <code className="bg-muted px-1 rounded">{property.default_value}</code></span>
                                  )}
                                </div>
                                {property.description && (
                                  <p className="text-xs text-muted-foreground mt-1">{property.description}</p>
                                )}
                              </div>
                            </div>
                            
                            {isMasterField && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedProperty({ name: property.name, tableName: entityType })}
                                  className="text-xs"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Manage Sources
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                {filteredProperties.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No properties found matching your search criteria.</p>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Property Source Management Modal */}
      <PropertySourceModal
        isOpen={!!selectedProperty}
        onClose={() => setSelectedProperty(null)}
        propertyName={selectedProperty?.name || ''}
        tableName={selectedProperty?.tableName || ''}
      />
    </div>
  )
}